import React, { FC, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Form, Icon } from 'semantic-ui-react';
import { BasicDialog } from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor, Size } from '@src/types';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { toLoggedMinutes } from './timeClockFormat';

const HOURS_TABLE = 'ConsultantSOWHours';
// Distinguishes hours entered from the SOW tab from payroll imports, which store the payroll BillableType.
const LOGGING_TYPE = 'Manual';
// An entry can span several days from its start date, so this is only a typo guard (matches LogHoursDialog).
const MAX_HOURS = 32767;

interface LogSowHoursDialogProps {
    sowId: string;
    sowName: string;
    consultantId: string;
    consultantName: string;
    budgetedHours: number | null;
    loggedHours: number;
    onClose: () => void;
    onSaved?: () => Promise<void> | void;
}

const todayIsoDate = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${ now.getMonth() + 1 }`.padStart( 2, '0' );
    const day = `${ now.getDate() }`.padStart( 2, '0' );
    return `${ year }-${ month }-${ day }`;
};

const parseNonNegativeInteger = ( value: string ): number | null => {
    const trimmed = value.trim();
    if( trimmed === '' ) return 0;
    if( !/^\d+$/.test( trimmed ) ) return null;
    const parsed = Number( trimmed );
    return Number.isSafeInteger( parsed ) ? parsed : null;
};

const formatHours = ( hours: number ): string =>
    ( Math.round( hours * 100 ) / 100 ).toLocaleString( undefined, { maximumFractionDigits: 2 } );

const LogSowHoursDialog: FC<LogSowHoursDialogProps> = ( {
    sowId,
    sowName,
    consultantId,
    consultantName,
    budgetedHours,
    loggedHours,
    onClose,
    onSaved,
} ) => {
    const { RunInsert } = useCentralCalls();

    const [ hoursInput, setHoursInput ]     = useState( '' ),
          [ minutesInput, setMinutesInput ] = useState( '' ),
          [ dateTracking, setDateTracking ] = useState( todayIsoDate() ),
          [ description, setDescription ]   = useState( '' ),
          [ isSaving, setIsSaving ]         = useState( false ),
          [ error, setError ]               = useState( '' );

    const parsedHours = parseNonNegativeInteger( hoursInput );
    const parsedMinutes = parseNonNegativeInteger( minutesInput );
    const pendingHours = parsedHours != null && parsedMinutes != null && parsedMinutes <= 59
        ? toLoggedMinutes( parsedHours, parsedMinutes ) / 60
        : 0;
    const remainingAfter = budgetedHours != null ? budgetedHours - loggedHours - pendingHours : null;

    const fail = ( message: string ) => {
        setError( message );
        return { errors: message };
    };

    const handleSubmit = async () => {
        if( isSaving ) return { errors: 'Saving...' };

        if( parsedHours == null || parsedHours > MAX_HOURS ){
            return fail( `Enter hours as a whole number from 0 to ${ MAX_HOURS }.` );
        }
        if( parsedMinutes == null || parsedMinutes > 59 ){
            return fail( 'Enter minutes as a whole number from 0 to 59.' );
        }

        const loggedMinutes = toLoggedMinutes( parsedHours, parsedMinutes );
        if( loggedMinutes < 1 ) return fail( 'Enter at least 1 minute.' );
        if( loggedMinutes > MAX_HOURS * 60 ) return fail( `Enter ${ MAX_HOURS } hours or fewer.` );
        if( !dateTracking ) return fail( 'Select the date the work started.' );

        setIsSaving( true );
        setError( '' );
        try {
            await RunInsert( HOURS_TABLE, {
                Id: uuid(),
                ConsultantId: consultantId,
                SOWId: sowId,
                LoggedMinutes: loggedMinutes,
                DateTracking: dateTracking,
                Description: description.trim() || null,
                LoggingType: LOGGING_TYPE,
            } );
            await onSaved?.();
            onClose();
        } catch( err ){
            console.error( '[LogSowHoursDialog] failed to log hours:', err );
            return fail( 'Could not log hours. Try again.' );
        } finally {
            setIsSaving( false );
        }
    };

    return (
        <BasicDialog
            showCancelButton
            showSubmitButton
            submitLabel={ isSaving ? 'Saving...' : 'Log hours' }
            submitColor={ SemanticUIColor.PURPLE }
            operationDisabled={ isSaving }
            successToastLabel={ 'Hours logged' }
            errorToastLabel={ '' }
            onCancel={ onClose }
            onSubmit={ handleSubmit }
            controlAlign={ Align.CENTER }
            className={ 'no-padding create-client-dialog log-hours-dialog' }
        >
            <BasicDialogContainer
                title={ `Log hours — ${ consultantName }` }
                titleColor={ SemanticUIColor.PURPLE }
                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                contentMinHeight={ '24dvh' }
                overflow
                onClose={ onClose }
            >
                <Form className={ 'create-client-form log-hours-form' }>
                    { error && (
                        <p className={ 'create-client-error' }>
                            <Icon name={ 'warning circle' } /> { error }
                        </p>
                    ) }

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>{ sowName }</span>
                        </div>

                        <p className={ `sow-log-hours-budget${ remainingAfter != null && remainingAfter < 0 ? ' is-over' : '' }` }>
                            <Icon name={ 'clock outline' } />
                            { budgetedHours == null
                                ? `${ formatHours( loggedHours ) } hrs logged so far. No budget set for this consultant.`
                                : remainingAfter != null && remainingAfter < 0
                                    ? `${ formatHours( loggedHours ) } of ${ formatHours( budgetedHours ) } hrs logged. This entry goes ${ formatHours( -remainingAfter ) } hrs over budget.`
                                    : `${ formatHours( loggedHours ) } of ${ formatHours( budgetedHours ) } hrs logged. ${ formatHours( remainingAfter ?? 0 ) } hrs left after this entry.` }
                        </p>

                        <div className={ 'log-hours-form-row' }>
                            <Form.Input
                                label={ 'Hours' }
                                type={ 'number' }
                                min={ 0 }
                                max={ MAX_HOURS }
                                step={ 1 }
                                value={ hoursInput }
                                placeholder={ '0' }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setHoursInput( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />

                            <Form.Input
                                label={ 'Minutes' }
                                type={ 'number' }
                                min={ 0 }
                                max={ 59 }
                                step={ 1 }
                                value={ minutesInput }
                                placeholder={ '0' }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setMinutesInput( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />

                            <Form.Input
                                className={ 'log-hours-date-field' }
                                label={ 'Start date' }
                                type={ 'date' }
                                value={ dateTracking }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setDateTracking( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />
                        </div>

                        <Form.TextArea
                            label={ 'Description' }
                            value={ description }
                            placeholder={ 'Optional description of the work' }
                            disabled={ isSaving }
                            rows={ 3 }
                            onChange={ ( _event, data ) => {
                                setDescription( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />
                    </div>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default LogSowHoursDialog;
